
import { defineComponent, inject, ref } from 'vue';
import { ISignupFormState } from '../signupFlow';
import { ISignupService, VERIFY_MODE } from '../signupService';
import { PhArrowLeft, PhArrowRight, PhEnvelopeOpen, PhShieldCheck } from '@phosphor-icons/vue';

export default defineComponent({
    name: 'StepVerifyEmail',
    components: { PhArrowLeft, PhArrowRight, PhEnvelopeOpen, PhShieldCheck },
    emits: ['next', 'back'],
    setup(props, { emit }) {
        const form = inject('signupForm') as ISignupFormState;
        const service = inject('signupService') as ISignupService;

        const code = ref(['', '', '', '', '', '']);
        const verifying = ref(false);
        const verified = ref(false);
        const resending = ref(false);
        const resent = ref(false);
        const errorMessage = ref('');

        async function submitCode() {
            verifying.value = true;
            errorMessage.value = '';
            const result = await service.verifyEmailCode(form, code.value.join(''));
            verifying.value = false;
            if (result.success) {
                verified.value = true;
                setTimeout(() => emit('next'), 1200);
            } else {
                errorMessage.value = result.reason || 'That code did not work. Please try again.';
                code.value = ['', '', '', '', '', ''];
                focusDigit(0);
            }
        }

        function focusDigit(index: number) {
            const el = document.getElementById(`verify-${index}`);
            if (el) el.focus();
        }

        function handleDigit(index: number, event: Event) {
            const value = (event.target as HTMLInputElement).value;
            if (!/^\d?$/.test(value)) {
                (event.target as HTMLInputElement).value = code.value[index];
                return;
            }
            code.value[index] = value;
            if (value && index < 5) focusDigit(index + 1);
            if (value && code.value.every(d => d !== '')) submitCode();
        }

        function handlePaste(event: ClipboardEvent) {
            const pasted = (event.clipboardData ? event.clipboardData.getData('text') : '')
                .replace(/\D/g, '')
                .slice(0, 6);
            if (!pasted.length) return;
            for (let i = 0; i < 6; i++) {
                code.value[i] = pasted[i] || '';
            }
            focusDigit(Math.min(pasted.length - 1, 5));
            if (code.value.every(d => d !== '')) submitCode();
        }

        function handleKeyDown(index: number, event: KeyboardEvent) {
            if (event.key === 'Backspace' && !code.value[index] && index > 0) {
                focusDigit(index - 1);
            }
        }

        async function handleResend() {
            resending.value = true;
            const result = await service.resendVerification(form);
            resending.value = false;
            if (result.success) {
                resent.value = true;
                setTimeout(() => (resent.value = false), 3000);
            }
        }

        return {
            form,
            verifyMode: VERIFY_MODE,
            code,
            verifying,
            verified,
            resending,
            resent,
            errorMessage,
            handleDigit,
            handlePaste,
            handleKeyDown,
            handleResend
        };
    }
});
